/**
 * Privacy Settings Tab
 *
 * Manages privacy preferences:
 * - Show last seen
 * - Show read receipts
 * - Profile visibility
 */

import { useState } from 'react';
import { useUserSettings, useUpdateSettings } from '@/hooks/useUserSettings';

export const PrivacySettings = () => {
  const { data: settings } = useUserSettings();
  const updateSettings = useUpdateSettings();

  const [showLastSeen, setShowLastSeen] = useState(true);
  const [showReadReceipts, setShowReadReceipts] = useState(true);
  const [profileVisibility, setProfileVisibility] = useState('all');

  // Sync form state from fetched settings (adjust state during render on change)
  const [syncedSettings, setSyncedSettings] = useState(settings);
  if (settings && settings !== syncedSettings) {
    setSyncedSettings(settings);
    setShowLastSeen(settings.show_last_seen);
    setShowReadReceipts(settings.show_read_receipts);
    setProfileVisibility(settings.profile_visibility);
  }

  const handleUpdate = async (field: string, value: string | number | boolean) => {
    try {
      await updateSettings.mutateAsync({ [field]: value });
    } catch (error) {
      console.error('Failed to update privacy settings:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Last Seen */}
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Показвай последно видян</div>
          <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Позволи на другите да виждат кога сте били активни за последно</div>
        </div>
        <button
          onClick={() => {
            const newValue = !showLastSeen;
            setShowLastSeen(newValue);
            handleUpdate('show_last_seen', newValue);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            showLastSeen ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-bg-tertiary'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              showLastSeen ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Read Receipts */}
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Показвай потвърждения за прочитане</div>
          <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Позволи на другите да знаят кога сте прочели съобщенията им</div>
        </div>
        <button
          onClick={() => {
            const newValue = !showReadReceipts;
            setShowReadReceipts(newValue);
            handleUpdate('show_read_receipts', newValue);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            showReadReceipts ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-bg-tertiary'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              showReadReceipts ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Profile Visibility */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
          Видимост на профила
        </h3>
        <div className="space-y-2">
          {[
            { value: 'all', label: 'Всички', desc: 'Всеки може да види вашия профил' },
            { value: 'team', label: 'Само екипа', desc: 'Само членове на екипа могат да видят вашия профил' },
          ].map((option) => (
            <label
              key={option.value}
              className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
            >
              <input
                type="radio"
                name="profile-visibility"
                value={option.value}
                checked={profileVisibility === option.value}
                onChange={(e) => {
                  setProfileVisibility(e.target.value);
                  handleUpdate('profile_visibility', e.target.value);
                }}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{option.label}</div>
                <div className="text-xs text-gray-500 dark:text-dark-text-secondary">{option.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="text-sm text-blue-900 dark:text-blue-200 font-medium">Съвет за поверителност</p>
            <p className="text-xs text-blue-800 dark:text-blue-300 mt-1">
              Някои настройки за поверителност може да повлияят как взаимодействате с членове на екипа и получавате известия.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;
